import * as THEOS from "../interfaces/theos.interface";
import { LogLevel, log } from "../utils/log";
import { IErrorMessage } from "../feim/types";
import { _configuration } from "../feim/configuration";
import { errorMessageDismissed } from "../feim/on";
import { sendGameEvent } from "../feim/sendGameEvent";
import { triggerEventInGame } from "../feim/registerEventHandler";

/**
 * An async alternative to send.errorMessage. Shows an error popup - using the remote error message system if used by the operator -
 * and resolves once the error popup is closed. Requires [[showErrorMessageCallback]] to be configured.
 *
 * Will trigger on.[[freeze]] when the error is shown, and on.[[unfreeze]] when the error is resolved.
 *
 * If the operator _is not_ handling error popups, the given [[showErrorMessageCallback]] is called. If [[showErrorMessageCallback]] returns a Promise and
 * it is resolved, the Promise returned by this function is resolved as well.
 * If [[showErrorMessageCallback]] does not return a Promise, the Promise returned by this function is resolved immediately.
 *
 * If the operator _is_ handling error popups, the module will send the error message data to the operator.
 * The promise returned by this function is resolved once that error message is dismissed in the operator's window.
 *
 * This is a convenience function, using send.errorMessage, on.errorMessageDisplayed and on.errorMessageDismissed internally.
 * 
 * ```
 * await showError(casinoResponseContainingError)
 * console.log("Error message closed, continue game")
 * ```
 * 
 * @param message - The casino response containing the error
 *
 * @returns A Promise which will be resolved when the operator popup is closed, or, if the operator isn't handling error popups, when the [[showErrorMessageCallback]] Promise is resolved.
 */
export const showError = async (message: IErrorMessage): Promise<void> =>
{
    // If config has remoteErrorMessages, send the message, wait for dismissed and resolve
    return new Promise((resolve, reject)=>
    {
        // No in-game error function defined: reject
        if (_configuration.showErrorMessageCallback === undefined)
        {
            log("showErrorMessageCallback is not defined", LogLevel.WARN);
            reject();
            return;
        }

        if (_configuration.operatorHandlesErrors)
        {
            log("Showing remote error message");

            // Remote error messages enabled: send the message, wait for dismissed and resolve

            sendGameEvent(THEOS.GameEvent.ERROR_MESSAGE, message);
            triggerEventInGame(THEOS.OperatorEvent.FREEZE);

            errorMessageDismissed(() =>
            {
                triggerEventInGame(THEOS.OperatorEvent.UNFREEZE);
                resolve();
            });
        }
        else
        {
            triggerEventInGame(THEOS.OperatorEvent.FREEZE);

            /**
             * Remote error messages are disabled: call showErrorMessageCallback, wait for dismissed (if a Promise)
             */
            Promise.resolve(_configuration.showErrorMessageCallback(message)).then((result) =>
            {
                triggerEventInGame(THEOS.OperatorEvent.UNFREEZE);
                resolve(result);
            }).catch((reason) =>
            {
                triggerEventInGame(THEOS.OperatorEvent.UNFREEZE);
                reject(reason);
            });
        }
    });
};
